import React from 'react';
import { useMedia } from '../../hooks/useMedia';
import { mq } from '../../global/breakpoints';

import { ExpandContentIcon } from '../../icons';

import { Box } from '../Box';
import { Text } from '../Text';
import { ButtonUnset } from '../ButtonUnset';
import { Grid } from '../Grid';
import { GridItem } from '../GridItem';
import { Stack } from '../Stack';
import { VisuallyHidden } from '../VisuallyHidden';

import { ImageGalleryDialog } from './_Dialog';

import * as styles from './gallery.css';

export type GalleryImageProps = {
  id: string;
  image?: React.ReactElement;
  title?: React.ReactElement;
  description?: React.ReactElement;
};

type ImageGalleryProps = {
  gallery: GalleryImageProps[];
};

export const ImageGallery = ({ gallery }: ImageGalleryProps) => {
  const isDesktop = useMedia(mq.md);
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedImage, setSelectedImage] = React.useState<number | undefined>(undefined);
  const refs = React.useRef<(HTMLButtonElement | null)[]>([]);

  const onCloseOutFocus = (event: Event, focusedIndex: number) => {
    event.preventDefault();
    refs.current[focusedIndex - 1]?.focus();
  };

  const onClickImage = (index: number) => {
    setSelectedImage(index);
    setIsOpen(true);
  };

  React.useEffect(() => {
    refs.current = gallery.map(() => null);
  }, [gallery]);

  if (!gallery.length) return null;

  return (
    <Box>
      <ImageGalleryDialog
        isOpen={isOpen}
        activeImage={selectedImage ?? null}
        gallery={gallery}
        handleOpenChange={() => setIsOpen(false)}
        handleOnCloseOutFocus={onCloseOutFocus}
      />
      {gallery ? (
        <Grid columns={{ initial: 1, md: 12 }} gap={{ initial: 4, md: 10 }}>
          {gallery?.map((item, index) => {
            const even = index % 2 === 0;

            if (!item.image) return null;

            return (
              <GridItem
                columnStart={even ? '1' : { initial: '1', md: '7' }}
                columnEnd={even ? { initial: '-1', md: '7' } : '-1'}
                key={`image-gallery-${item.id}`}
              >
                {isDesktop ? (
                  <Stack isFullHeight isFullWidth>
                    <ButtonUnset
                      id={`image-gallery-${item.id}`}
                      onClick={() => onClickImage(index)}
                      ref={(el) => {
                        refs.current[index] = el;
                      }}
                      className={styles.imageButton}
                    >
                      {item.image}
                      <VisuallyHidden>{item.title}</VisuallyHidden>
                      <Box
                        className={styles.imageButtonIcon}
                        borderRadius="circle"
                        bg="backgroundLight"
                        width="48px"
                        height="48px"
                        asChild
                      >
                        <Stack alignX="center" alignY="center">
                          <ExpandContentIcon />
                        </Stack>
                      </Box>
                    </ButtonUnset>
                  </Stack>
                ) : (
                  <Stack gap={2} isFullWidth>
                    <div aria-describedby={`image-gallery-${item.id}`}>{item.image}</div>
                    <Box id={`image-gallery-${item.id}`}>
                      <Text>{item.description}</Text>
                    </Box>
                  </Stack>
                )}
              </GridItem>
            );
          })}
        </Grid>
      ) : null}
    </Box>
  );
};

